/* RK Threads — MS Paint Designer
   Konva-based drawing widget with raster fill + vector strokes,
   compliance gate, and upload to Shopify Files via Cloudflare Worker. */

(function () {
  "use strict";

  const STAGE_SIZE = 1500; // logical canvas units
  const HISTORY_LIMIT = 30;

  // Wait for Konva (loaded async via defer) then bootstrap.
  function ready() {
    if (typeof window.Konva === "undefined") {
      setTimeout(ready, 50);
      return;
    }
    const root = document.getElementById("ms-paint-root");
    if (!root || root.dataset.bootstrapped === "1") return;
    root.dataset.bootstrapped = "1";
    init(root);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready);
  } else {
    ready();
  }

  function init(root) {
    const cfg = readConfig(root);
    const state = {
      tool: "pencil",
      color: cfg.palette[0],
      size: cfg.brushSizes[1] || cfg.brushSizes[0],
      drawing: false,
      currentStroke: null,
      currentShape: null,
      shapeStart: null,
      undoStack: [],
      redoStack: [],
      hasDrawn: false,
      saving: false,
    };

    const stageEl = document.getElementById("ms-paint-stage");
    const stage = new window.Konva.Stage({
      container: stageEl,
      width: STAGE_SIZE,
      height: STAGE_SIZE,
    });
    fitStage(stage, stageEl);
    window.addEventListener("resize", () => fitStage(stage, stageEl));

    // White background layer (so the PNG export has a paper background).
    const bgLayer = new window.Konva.Layer({ listening: false });
    bgLayer.add(new window.Konva.Rect({ x: 0, y: 0, width: STAGE_SIZE, height: STAGE_SIZE, fill: "#ffffff" }));
    stage.add(bgLayer);

    // Raster layer (flood-fill + eraser composite). Backed by an offscreen canvas.
    const rasterCanvas = document.createElement("canvas");
    rasterCanvas.width = STAGE_SIZE;
    rasterCanvas.height = STAGE_SIZE;
    const rasterCtx = rasterCanvas.getContext("2d", { willReadFrequently: true });
    rasterCtx.clearRect(0, 0, STAGE_SIZE, STAGE_SIZE);
    const rasterLayer = new window.Konva.Layer();
    const rasterImage = new window.Konva.Image({ image: rasterCanvas, x: 0, y: 0, width: STAGE_SIZE, height: STAGE_SIZE });
    rasterLayer.add(rasterImage);
    stage.add(rasterLayer);

    // Vector layer (pencil + shape strokes).
    const vectorLayer = new window.Konva.Layer();
    stage.add(vectorLayer);

    buildSizes(root, cfg, state);
    buildPalette(root, cfg, state);
    wireTools(root, state);
    wireStage(stage, state, vectorLayer, rasterLayer, rasterCanvas, rasterCtx);
    wireUndoRedoClear(root, state, vectorLayer, rasterCtx, rasterLayer);
    wireSave(root, cfg, state, stage, vectorLayer, rasterCanvas);
    injectCartFormInputs(root, cfg);
    pushHistory(state, vectorLayer, rasterCanvas);
    setStatus(root, "Ready");
  }

  function readConfig(root) {
    const d = root.dataset;
    return {
      uploadEndpoint: d.uploadEndpoint || "",
      turnstileSiteKey: d.turnstileSiteKey || "",
      outputResolution: parseInt(d.outputResolution, 10) || 1500,
      palette: (d.palette || "#000000")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      brushSizes: (d.brushSizes || "8,16,32,64")
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0),
      minStroke: parseInt(d.minStroke, 10) || 8,
      cartFormSelector: d.cartFormSelector || "form[action^='/cart/add']",
      svgEnabled: d.svgEnabled === "true",
      shapeToolEnabled: d.shapeToolEnabled === "true",
    };
  }

  function fitStage(stage, container) {
    const rect = container.getBoundingClientRect();
    const size = Math.max(1, Math.min(rect.width, rect.height));
    stage.width(size);
    stage.height(size);
    const scale = size / STAGE_SIZE;
    stage.scale({ x: scale, y: scale });
    stage.batchDraw();
  }

  function buildSizes(root, cfg, state) {
    const wrap = root.querySelector(".ms-paint-sizes");
    wrap.innerHTML = "";
    cfg.brushSizes.forEach((px, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ms-paint-size";
      btn.setAttribute("aria-label", `Brush ${px}px`);
      btn.setAttribute("aria-pressed", String(px === state.size));
      const dot = document.createElement("span");
      dot.className = "ms-paint-size__dot";
      // Display dot relative to brush size, capped so the smallest is still visible.
      const display = Math.max(4, Math.min(18, Math.round(px / 4)));
      dot.style.width = display + "px";
      dot.style.height = display + "px";
      btn.appendChild(dot);
      btn.addEventListener("click", () => {
        state.size = Math.max(cfg.minStroke, px);
        wrap.querySelectorAll(".ms-paint-size").forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");
      });
      wrap.appendChild(btn);
      if (idx === 1) state.size = Math.max(cfg.minStroke, px);
    });
  }

  function buildPalette(root, cfg, state) {
    const wrap = root.querySelector(".ms-paint-palette");
    wrap.innerHTML = "";
    cfg.palette.forEach((hex, idx) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ms-paint-swatch";
      btn.setAttribute("aria-label", `Colour ${hex}`);
      btn.setAttribute("aria-pressed", String(idx === 0));
      // !important inline so the Win95 site overlay's global button rule
      // can't paint over the real swatch colour.
      btn.style.setProperty("background", hex, "important");
      btn.style.setProperty("background-color", hex, "important");
      btn.addEventListener("click", () => {
        state.color = hex;
        wrap.querySelectorAll(".ms-paint-swatch").forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");
      });
      wrap.appendChild(btn);
    });
  }

  function wireTools(root, state) {
    const toolButtons = root.querySelectorAll(".ms-paint-tool[data-tool]");
    toolButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        state.tool = btn.dataset.tool;
        toolButtons.forEach((b) => b.setAttribute("aria-pressed", "false"));
        btn.setAttribute("aria-pressed", "true");
      });
    });
  }

  function wireStage(stage, state, vectorLayer, rasterLayer, rasterCanvas, rasterCtx) {
    stage.on("mousedown touchstart", (e) => {
      e.evt.preventDefault();
      const pos = getStagePos(stage);
      if (!pos) return;
      if (state.tool === "fill") {
        floodFill(stage, rasterCtx, rasterCanvas, pos, state.color);
        rasterLayer.batchDraw();
        state.hasDrawn = true;
        pushHistory(state, vectorLayer, rasterCanvas);
        notifyDrawn();
        return;
      }
      state.drawing = true;
      if (state.tool === "pencil" || state.tool === "eraser") {
        const isEraser = state.tool === "eraser";
        const stroke = new window.Konva.Line({
          stroke: isEraser ? "#ffffff" : state.color,
          strokeWidth: state.size,
          globalCompositeOperation: isEraser ? "destination-out" : "source-over",
          lineCap: "round",
          lineJoin: "round",
          points: [pos.x, pos.y, pos.x, pos.y],
          tension: 0.4,
        });
        vectorLayer.add(stroke);
        state.currentStroke = stroke;
      } else if (state.tool === "line" || state.tool === "rect" || state.tool === "circle") {
        state.shapeStart = pos;
        const common = {
          stroke: state.color,
          strokeWidth: state.size,
          lineCap: "round",
          lineJoin: "round",
        };
        if (state.tool === "line") {
          state.currentShape = new window.Konva.Line({ ...common, points: [pos.x, pos.y, pos.x, pos.y] });
        } else if (state.tool === "rect") {
          state.currentShape = new window.Konva.Rect({ ...common, x: pos.x, y: pos.y, width: 0, height: 0 });
        } else {
          state.currentShape = new window.Konva.Ellipse({ ...common, x: pos.x, y: pos.y, radiusX: 0, radiusY: 0 });
        }
        vectorLayer.add(state.currentShape);
      }
    });

    stage.on("mousemove touchmove", (e) => {
      if (!state.drawing) return;
      e.evt.preventDefault();
      const pos = getStagePos(stage);
      if (!pos) return;
      if (state.currentStroke) {
        const pts = state.currentStroke.points().concat([pos.x, pos.y]);
        state.currentStroke.points(pts);
        vectorLayer.batchDraw();
      } else if (state.currentShape && state.shapeStart) {
        if (state.tool === "line") {
          state.currentShape.points([state.shapeStart.x, state.shapeStart.y, pos.x, pos.y]);
        } else if (state.tool === "rect") {
          state.currentShape.width(pos.x - state.shapeStart.x);
          state.currentShape.height(pos.y - state.shapeStart.y);
        } else {
          state.currentShape.radiusX(Math.abs(pos.x - state.shapeStart.x));
          state.currentShape.radiusY(Math.abs(pos.y - state.shapeStart.y));
        }
        vectorLayer.batchDraw();
      }
    });

    const finish = () => {
      if (!state.drawing) return;
      state.drawing = false;
      if (state.currentStroke || state.currentShape) {
        state.hasDrawn = true;
        notifyDrawn();
        pushHistory(state, vectorLayer, rasterCanvas);
      }
      state.currentStroke = null;
      state.currentShape = null;
      state.shapeStart = null;
    };
    stage.on("mouseup touchend mouseleave", finish);
    window.addEventListener("mouseup", finish);
  }

  function getStagePos(stage) {
    const p = stage.getPointerPosition();
    if (!p) return null;
    const scale = stage.scaleX() || 1;
    return { x: p.x / scale, y: p.y / scale };
  }

  // 4-connected flood fill on the raster canvas at fill point.
  // Reads from a flattened composite of bg + raster + vector so that vector
  // strokes act as fill boundaries.
  function floodFill(stage, rasterCtx, rasterCanvas, point, hex) {
    const composite = compositeSnapshot(stage);
    const cw = composite.width;
    const ch = composite.height;
    const compData = composite.ctx.getImageData(0, 0, cw, ch);
    const targetIdx = (Math.round(point.y) * cw + Math.round(point.x)) * 4;
    const target = [compData.data[targetIdx], compData.data[targetIdx + 1], compData.data[targetIdx + 2], compData.data[targetIdx + 3]];
    const fill = hexToRgba(hex);
    if (colourMatch(target, fill, 1)) return; // already that colour

    // Paint onto the raster layer's image data.
    const rasterData = rasterCtx.getImageData(0, 0, cw, ch);
    const stack = [[Math.round(point.x), Math.round(point.y)]];
    const visited = new Uint8Array(cw * ch);
    while (stack.length) {
      const [x, y] = stack.pop();
      if (x < 0 || y < 0 || x >= cw || y >= ch) continue;
      const pIdx = y * cw + x;
      if (visited[pIdx]) continue;
      visited[pIdx] = 1;
      const i = pIdx * 4;
      const cur = [compData.data[i], compData.data[i + 1], compData.data[i + 2], compData.data[i + 3]];
      if (!colourMatch(cur, target, 8)) continue;
      rasterData.data[i] = fill[0];
      rasterData.data[i + 1] = fill[1];
      rasterData.data[i + 2] = fill[2];
      rasterData.data[i + 3] = 255;
      // Mark composite as filled so the boundary check works for neighbours.
      compData.data[i] = fill[0];
      compData.data[i + 1] = fill[1];
      compData.data[i + 2] = fill[2];
      compData.data[i + 3] = 255;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    rasterCtx.putImageData(rasterData, 0, 0);
  }

  // Flatten the current stage to a 1500x1500 canvas so flood-fill can use
  // vector strokes as boundaries.
  function compositeSnapshot(stage) {
    const off = document.createElement("canvas");
    off.width = STAGE_SIZE;
    off.height = STAGE_SIZE;
    const octx = off.getContext("2d");
    const flat = stage.toCanvas({ pixelRatio: STAGE_SIZE / stage.width() });
    octx.drawImage(flat, 0, 0, STAGE_SIZE, STAGE_SIZE);
    return { ctx: octx, width: STAGE_SIZE, height: STAGE_SIZE };
  }

  function hexToRgba(hex) {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return [r, g, b, 255];
  }

  function colourMatch(a, b, tol) {
    return Math.abs(a[0] - b[0]) <= tol && Math.abs(a[1] - b[1]) <= tol && Math.abs(a[2] - b[2]) <= tol && Math.abs(a[3] - b[3]) <= tol;
  }

  function wireUndoRedoClear(root, state, vectorLayer, rasterCtx, rasterLayer) {
    root.querySelector('[data-action="undo"]').addEventListener("click", () => {
      if (state.undoStack.length < 2) return;
      const cur = state.undoStack.pop();
      state.redoStack.push(cur);
      const prev = state.undoStack[state.undoStack.length - 1];
      restoreSnapshot(prev, vectorLayer, rasterCtx, rasterLayer);
    });
    root.querySelector('[data-action="redo"]').addEventListener("click", () => {
      const next = state.redoStack.pop();
      if (!next) return;
      state.undoStack.push(next);
      restoreSnapshot(next, vectorLayer, rasterCtx, rasterLayer);
    });
    root.querySelector('[data-action="clear"]').addEventListener("click", () => {
      vectorLayer.destroyChildren();
      vectorLayer.batchDraw();
      rasterCtx.clearRect(0, 0, STAGE_SIZE, STAGE_SIZE);
      rasterLayer.batchDraw();
      pushHistory(state, vectorLayer, null);
    });
  }

  function pushHistory(state, vectorLayer, rasterCanvasOrNull) {
    const snap = {
      vector: vectorLayer.toJSON(),
      raster: rasterCanvasOrNull ? rasterCanvasOrNull.toDataURL("image/png") : null,
    };
    state.undoStack.push(snap);
    if (state.undoStack.length > HISTORY_LIMIT) state.undoStack.shift();
    state.redoStack.length = 0;
  }

  function restoreSnapshot(snap, vectorLayer, rasterCtx, rasterLayer) {
    vectorLayer.destroyChildren();
    const restored = window.Konva.Node.create(snap.vector);
    if (restored && restored.getChildren) {
      restored.getChildren().forEach((c) => {
        vectorLayer.add(c.clone());
      });
    }
    vectorLayer.batchDraw();
    rasterCtx.clearRect(0, 0, STAGE_SIZE, STAGE_SIZE);
    if (snap.raster) {
      const img = new Image();
      img.onload = () => {
        rasterCtx.drawImage(img, 0, 0);
        rasterLayer.batchDraw();
      };
      img.src = snap.raster;
    } else {
      rasterLayer.batchDraw();
    }
  }

  function wireSave(root, cfg, state, stage, vectorLayer, rasterCanvas) {
    const checkbox = root.querySelector("#ms-paint-compliance");
    const button = root.querySelector("#ms-paint-save");
    const label = button.querySelector(".ms-paint-save__label");
    const hourglass = button.querySelector(".ms-paint-hourglass");
    const savedIndicator = root.querySelector("#ms-paint-saved");
    const errorDialog = root.querySelector("#ms-paint-error");

    const setLabel = (text) => { if (label) label.textContent = text; };
    const showHourglass = (show) => { if (hourglass) hourglass.hidden = !show; };

    const updateEnabled = () => {
      button.disabled = state.saving || !checkbox.checked || !state.hasDrawn;
    };
    checkbox.addEventListener("change", updateEnabled);
    root.addEventListener("ms-paint:drawn", updateEnabled);
    errorDialog.querySelector("[data-dialog-dismiss]").addEventListener("click", () => {
      errorDialog.hidden = true;
    });

    // Wire the "Crash my computer" easter-egg button.
    root.querySelectorAll('[data-action="crash"]').forEach((btn) => {
      btn.addEventListener("click", () => {
        if (window.RKThreadsWin95 && typeof window.RKThreadsWin95.crash === "function") {
          window.RKThreadsWin95.crash();
        } else {
          console.warn("[ms-paint] Win95 overlay not loaded; crash unavailable.");
        }
      });
    });

    button.addEventListener("click", async () => {
      if (button.disabled) return;
      state.saving = true;
      button.classList.add("ms-paint-btn--busy");
      setLabel("Saving");
      showHourglass(true);
      updateEnabled();
      setStatus(root, "Generating image...");

      try {
        const pngBlob = await exportPng(stage, cfg.outputResolution);
        const svgBlob = cfg.svgEnabled ? exportSvg(vectorLayer, rasterCanvas) : null;
        const token = cfg.turnstileSiteKey ? await getTurnstileToken(cfg.turnstileSiteKey) : "dev-no-token";

        setStatus(root, "Uploading...");
        const result = await uploadDesign(cfg.uploadEndpoint, pngBlob, svgBlob, token);

        setProperty(root, "png", result.pngUrl);
        setProperty(root, "svg", result.svgUrl || "");
        setProperty(root, "id", result.designId);

        if (savedIndicator) savedIndicator.hidden = false;
        setLabel("Re-save design");
        setStatus(root, "Design saved");
        enableAddToCart(cfg.cartFormSelector);
      } catch (err) {
        console.error("[ms-paint] save failed", err);
        showError(errorDialog, err && err.message ? err.message : "Couldn't save your design. Please try again.");
        setLabel("Save design");
        setStatus(root, "Error");
      } finally {
        state.saving = false;
        button.classList.remove("ms-paint-btn--busy");
        showHourglass(false);
        updateEnabled();
      }
    });

    // Block the add-to-cart form submission until a design is saved.
    document.querySelectorAll(cfg.cartFormSelector).forEach((form) => {
      form.addEventListener("submit", (e) => {
        const png = form.querySelector('input[name="properties[Design]"]');
        if (!png || !png.value) {
          e.preventDefault();
          e.stopPropagation();
          showError(errorDialog, "Please draw and save a design before adding to cart.");
        }
      });
    });
    disableAddToCart(cfg.cartFormSelector);
  }

  function exportPng(stage, outputResolution) {
    return new Promise((resolve, reject) => {
      const canvas = stage.toCanvas({ pixelRatio: outputResolution / stage.width() });
      if (canvas.toBlob) {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))), "image/png");
      } else {
        reject(new Error("Canvas.toBlob unsupported"));
      }
    });
  }

  // Vector strokes -> SVG polylines. Raster layer embedded as base64 <image>.
  // Flood-fills aren't true vectors; this is documented in the handover.
  function exportSvg(vectorLayer, rasterCanvas) {
    const parts = [];
    parts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${STAGE_SIZE} ${STAGE_SIZE}" width="${STAGE_SIZE}" height="${STAGE_SIZE}">`);
    parts.push(`<rect width="100%" height="100%" fill="#ffffff"/>`);
    parts.push(`<image href="${rasterCanvas.toDataURL("image/png")}" x="0" y="0" width="${STAGE_SIZE}" height="${STAGE_SIZE}"/>`);
    vectorLayer.getChildren().forEach((node) => {
      const cls = node.getClassName();
      const stroke = node.stroke();
      const sw = node.strokeWidth();
      if (cls === "Line") {
        const pts = node.points();
        if (pts.length < 2) return;
        const isClosed = node.closed && node.closed();
        const path = pts.reduce((acc, v, i) => acc + (i % 2 === 0 ? (i === 0 ? "M" : "L") + v : "," + v), "");
        parts.push(`<path d="${path}${isClosed ? "Z" : ""}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`);
      } else if (cls === "Rect") {
        parts.push(`<rect x="${node.x()}" y="${node.y()}" width="${node.width()}" height="${node.height()}" stroke="${stroke}" stroke-width="${sw}" fill="none"/>`);
      } else if (cls === "Ellipse") {
        parts.push(`<ellipse cx="${node.x()}" cy="${node.y()}" rx="${node.radiusX()}" ry="${node.radiusY()}" stroke="${stroke}" stroke-width="${sw}" fill="none"/>`);
      }
    });
    parts.push(`</svg>`);
    return new Blob([parts.join("")], { type: "image/svg+xml" });
  }

  async function getTurnstileToken(siteKey) {
    if (!window.turnstile) {
      await new Promise((resolve) => {
        const start = Date.now();
        const poll = () => {
          if (window.turnstile) return resolve();
          if (Date.now() - start > 8000) return resolve();
          setTimeout(poll, 100);
        };
        poll();
      });
    }
    if (!window.turnstile) return "";
    return await new Promise((resolve) => {
      const host = document.createElement("div");
      host.style.display = "none";
      document.body.appendChild(host);
      window.turnstile.render(host, {
        sitekey: siteKey,
        size: "invisible",
        callback: (token) => resolve(token),
        "error-callback": () => resolve(""),
      });
      window.turnstile.execute(host);
    });
  }

  async function uploadDesign(endpoint, png, svg, token) {
    const form = new FormData();
    form.append("png", png, "design.png");
    if (svg) form.append("svg", svg, "design.svg");
    form.append("token", token);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(endpoint, { method: "POST", body: form, signal: controller.signal });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`Upload failed (${res.status}). ${body}`.trim());
      }
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  function injectCartFormInputs(root, cfg) {
    const tpl = root.querySelector("#ms-paint-prop-template");
    document.querySelectorAll(cfg.cartFormSelector).forEach((form) => {
      tpl.content.childNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const clone = node.cloneNode(true);
          form.appendChild(clone);
        }
      });
    });
  }

  function setProperty(root, key, value) {
    const cfg = readConfig(root);
    document.querySelectorAll(cfg.cartFormSelector).forEach((form) => {
      const input = form.querySelector(`input[data-prop="${key}"]`);
      if (input) input.value = value;
    });
  }

  function enableAddToCart(selector) {
    document.querySelectorAll(selector).forEach((form) => {
      form.querySelectorAll('button[type="submit"], [name="add"]').forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute("aria-disabled");
      });
    });
  }

  function disableAddToCart(selector) {
    document.querySelectorAll(selector).forEach((form) => {
      form.querySelectorAll('button[type="submit"], [name="add"]').forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute("aria-disabled", "true");
      });
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector(".ms-paint-status__msg");
    if (el) el.textContent = msg;
  }

  function showError(dialog, msg) {
    dialog.querySelector(".ms-paint-dialog__msg").textContent = msg;
    dialog.hidden = false;
  }

  function notifyDrawn() {
    const root = document.getElementById("ms-paint-root");
    if (root) root.dispatchEvent(new CustomEvent("ms-paint:drawn"));
  }
})();
